import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock } from "lucide-react";
import { API, HOME_PASTOR_CAROUSEL, WORSHIP_CAROUSEL, TestimonyCarousel, LiveAlertOptIn } from "../shared";

const HomePage = () => {
  const [nextService, setNextService] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [pastorSlide, setPastorSlide] = useState(0);
  const [worshipSlide, setWorshipSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setPastorSlide((s) => (s + 1) % HOME_PASTOR_CAROUSEL.length), 5000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setWorshipSlide((s) => (s + 1) % WORSHIP_CAROUSEL.length), 5000);
    return () => clearInterval(id);
  }, []);
  const [connectForm, setConnectForm] = useState({ name: "", email: "", phone: "", is_new_visitor: false });
  const [submitting, setSubmitting] = useState(false);

  const homeMinistries = [
    { id: "jesus-encounter", name: "Jesus Encounters", description: "Our on-fire young adults gathering every Tuesday night.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/zzqdf7ge_7C4B8057-3AA1-4565-A83E-8DEC380773CA.png" },
    { id: "kingdom-academy", name: "Kingdom Academy", description: "Raising the next generation with the love and Word of God — Levels 1–5.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/s8rz7dok_ae7b8b49-616b-40e4-b685-d7e229cebc73.jpeg" },
    { id: "life-groups", name: "Life Groups", description: "Doing life together in homes — community, the Word, and prayer in smaller circles.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/62fz9ot5_main%20heo%20image%20.jpeg" },
    { id: "food-giveaway", name: "Food Giveaway", description: "Serving our community through monthly food distributions.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/qx2g7ov6_Food%201.jpeg" },
    { id: "discipleship", name: "Discipleship Training", description: "Growing deeper in faith through Bible study and mentorship.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/vq2kfk4g_received_932252378839245.jpeg" },
    { id: "missions", name: "Global Missions", description: "Led by Apostle Kem & Dara Gaskin, Senior Overseers — extending WHCO's reach across Tijuana, Brazil, and Africa.", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/nsm861kr_IMG_5812.jpg" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceRes = await axios.get(`${API}/next-service`);
        setNextService(serviceRes.data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("next-service fetch failed (countdown hidden):", err?.message || err);
        }
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!nextService?.datetime) return;
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(nextService.datetime).getTime();
      const diff = target - now;
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [nextService]);

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/connect`, { ...connectForm, card_type: "connect" });
      toast.success("Thank you for connecting with us!");
      setConnectForm({ name: "", email: "", phone: "", is_new_visitor: false });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-in">
      <section className="relative min-h-screen flex items-center justify-center bg-black" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url('/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/5rdi83no_main%20heo%20image%20.jpeg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black" />
        <div className="relative z-10 max-w-5xl mx-auto px-8 text-center text-white">
          <p className="heading-caps text-xs text-white/60 mb-8 tracking-[0.3em]">Welcome to</p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl mb-8 leading-tight" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }} data-testid="hero-title">
            World Harvest<br /><span className="italic">Christian Outreach</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mb-12 max-w-2xl mx-auto font-light">
            Making Disciples, Raising Up Leaders, Launching to the Nations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-20">
            <Link to="/contact" className="btn-primary" data-testid="hero-plan-visit">Plan Your Visit</Link>
            <Link to="/about" className="btn-outline" data-testid="hero-im-new">I&apos;m New</Link>
          </div>
          {nextService?.datetime && (
            <div>
              <p className="heading-caps text-xs text-white/40 mb-6 tracking-[0.2em]">Next Service: {nextService?.name}</p>
              <div className="flex justify-center gap-6" data-testid="service-countdown">
                {[
                  { value: countdown.days, label: "Days" },
                  { value: countdown.hours, label: "Hours" },
                  { value: countdown.minutes, label: "Min" },
                  { value: countdown.seconds, label: "Sec" },
                ].map((item) => (
                  <div key={item.label} className="countdown-box px-6 py-4 rounded-sm">
                    <div className="text-3xl font-light">{String(item.value).padStart(2, '0')}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative" data-testid="home-pastor-carousel">
              <div className="relative aspect-[4/5] overflow-hidden bg-black shadow-2xl">
                {HOME_PASTOR_CAROUSEL.map((src, i) => (
                  <img key={src} src={src} alt={`Pastor Eddie Barragan ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === pastorSlide ? "opacity-100" : "opacity-0"}`}
                    data-testid={`home-pastor-slide-${i}`}
                  />
                ))}
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3 z-10">
                  {HOME_PASTOR_CAROUSEL.map((src, i) => (
                    <button key={src} onClick={() => setPastorSlide(i)} aria-label={`Go to slide ${i + 1}`}
                      className={`h-2 rounded-full transition-all duration-300 ${i === pastorSlide ? "bg-white w-8" : "bg-white/50 hover:bg-white/80 w-2"}`}
                      data-testid={`home-pastor-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Our Pastors</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Eddie & Yadira<br /><span className="italic">Barragan</span>
              </h2>
              <p className="text-lg text-black/60 mb-8 leading-relaxed">
                World Harvest Christian Outreach is more than a church – it&apos;s a family committed to
                transforming lives through the power of the Gospel. Led by Pastors Eddie and Yadira,
                our community has been serving Lancaster for over 20 years.
              </p>
              <Link to="/about" className="btn-dark inline-block">Learn More</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-black text-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-white/40 mb-8 tracking-[0.2em]">Our Mission</p>
          <h2 className="text-3xl md:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}>
            &ldquo;Making Disciples,<br />Raising Up Leaders,<br /><span className="italic">Launching to the Nations&rdquo;</span>
          </h2>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Get Involved</p>
              <h2 className="text-4xl md:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>Ministries</h2>
            </div>
            <Link to="/ministries" className="btn-dark mt-6 md:mt-0">View All</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="ministries-grid">
            {homeMinistries.map((ministry) => (
              <Link to="/ministries" key={ministry.id} className="group" data-testid={`ministry-card-${ministry.id}`}>
                <div className="aspect-[4/5] overflow-hidden mb-6">
                  <img src={ministry.image} alt={ministry.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  />
                </div>
                <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{ministry.name}</h3>
                <p className="text-sm text-black/50 line-clamp-2">{ministry.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Worship & Word</p>
              <h2 className="text-4xl md:text-5xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Experience God&apos;s<br /><span className="italic">Presence</span>
              </h2>
              <p className="text-lg text-black/60 mb-8 leading-relaxed">
                Join us for powerful worship and life-changing messages. Whether you&apos;re seeking answers,
                looking for community, or wanting to grow deeper in your faith, there&apos;s a place for you here in Lancaster.
              </p>
              <div className="flex gap-4">
                <Link to="/media" className="btn-dark">Watch Sermons</Link>
                <Link to="/events" className="btn-dark" style={{ background: 'transparent', border: '1px solid black', color: 'black' }}>Upcoming Events</Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative aspect-[4/5] overflow-hidden bg-black shadow-2xl" data-testid="home-worship-carousel">
                {WORSHIP_CAROUSEL.map((src, i) => (
                  <img key={src} src={src} alt={`WHCO Worship ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === worshipSlide ? "opacity-100" : "opacity-0"}`}
                    data-testid={`home-worship-slide-${i}`}
                  />
                ))}
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3 z-10">
                  {WORSHIP_CAROUSEL.map((src, i) => (
                    <button key={src} onClick={() => setWorshipSlide(i)} aria-label={`Worship slide ${i + 1}`}
                      className={`h-2 rounded-full transition-all duration-300 ${i === worshipSlide ? "bg-white w-8" : "bg-white/50 hover:bg-white/80 w-2"}`}
                      data-testid={`home-worship-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TestimonyCarousel />

      <section className="bg-white px-8 pb-24" data-testid="home-live-alert">
        <div className="max-w-5xl mx-auto border border-black/10">
          <LiveAlertOptIn variant="light" source="home" testId="home-live-optin" />
        </div>
      </section>

      <section className="py-32 bg-black text-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-20">
            <div>
              <p className="heading-caps text-xs text-white/40 mb-4 tracking-[0.2em]">Connect</p>
              <h2 className="text-4xl md:text-5xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                We&apos;d Love to<br /><span className="italic">Hear From You</span>
              </h2>
              <p className="text-lg text-white/60 mb-8 leading-relaxed">
                Whether you&apos;re visiting for the first time or looking to get more involved,
                fill out our connect card and someone from our team will reach out.
              </p>
              <div className="space-y-6 text-sm">
                <div className="flex items-start gap-4">
                  <MapPin className="text-white/40 mt-1" size={18} />
                  <div><p className="text-white">224 W Lancaster Blvd</p><p className="text-white/60">Lancaster, CA 93534</p></div>
                </div>
                <div className="flex items-center gap-4">
                  <Clock className="text-white/40" size={18} />
                  <div><p className="text-white">Sunday 10:00 AM – 12:00 PM • Wednesday 7:00 PM</p></div>
                </div>
              </div>
            </div>

            <form onSubmit={handleConnectSubmit} className="space-y-6" data-testid="connect-form">
              <input type="text" value={connectForm.name} onChange={(e) => setConnectForm({ ...connectForm, name: e.target.value })}
                className="w-full px-0 py-4 bg-transparent border-b border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none"
                placeholder="Your Name" required data-testid="connect-name"
              />
              <input type="email" value={connectForm.email} onChange={(e) => setConnectForm({ ...connectForm, email: e.target.value })}
                className="w-full px-0 py-4 bg-transparent border-b border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none"
                placeholder="Email Address" required data-testid="connect-email"
              />
              <input type="tel" value={connectForm.phone} onChange={(e) => setConnectForm({ ...connectForm, phone: e.target.value })}
                className="w-full px-0 py-4 bg-transparent border-b border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none"
                placeholder="Phone (Optional)" data-testid="connect-phone"
              />
              <div className="flex items-center gap-3 py-4">
                <input type="checkbox" id="newVisitor" checked={connectForm.is_new_visitor}
                  onChange={(e) => setConnectForm({ ...connectForm, is_new_visitor: e.target.checked })}
                  className="w-4 h-4 bg-transparent border border-white/40" data-testid="connect-new-visitor"
                />
                <label htmlFor="newVisitor" className="text-sm text-white/60">I&apos;m a first-time visitor</label>
              </div>
              <button type="submit" disabled={submitting} className="w-full btn-primary mt-4 disabled:opacity-50" data-testid="connect-submit">
                {submitting ? "Sending..." : "Connect With Us"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Join Us</p>
          <h2 className="text-4xl md:text-5xl mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>Service Times</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="p-12 bg-neutral-100">
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Sunday</p>
              <p className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>10:00 AM – 12:00 PM</p>
              <p className="text-sm text-black/50 mt-2">Morning Worship</p>
            </div>
            <div className="p-12 bg-black text-white">
              <p className="heading-caps text-xs text-white/40 mb-4 tracking-[0.2em]">Wednesday</p>
              <p className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>7:00 PM</p>
              <p className="text-sm text-white/50 mt-2">Bible Study</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
